import './App.css';
import React, { useState, useEffect } from 'react';
import { useGetSetting, useGetSettingsList, useSaveSetting, useDeleteSettings } from './hooks/demoAPI';
import Header from './components/header'
import { useOptionSelect } from './components/optionSelect';
import Loading from './components/loading';
import { useProxySettingsEditor } from './components/proxySettingsEdit';
import { BLANK_PROXY_SETTINGS } from './utils/types';
import { FormWithMargin } from './styles/styledComponents';
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { UploadDialog } from './components/uploadDialog';
import { useUserPrompt } from './components/promptUser';
import AuditLogs from './components/AuditLogs';
import useRestTriggerConfig from './components/restTriggers';

const createNewOption = { name: "Create New", value: "Create New" };
function ProxyConfigApp() {
    //API hooks
    const [openUploadDialog, setOpenUploadDialog] = React.useState(false);
    const closeUploadDialog = () => setOpenUploadDialog(false);
    const [displayErrors, setDisplayErrors] = React.useState(false);
    const [deleteFlag, setDeleteFlag] = React.useState(false);
    const [uploadFlag, setUploadFlag] = React.useState(false);
    const {
        settingsList,
        loading: isSettingsListLoading,
        refresh: refreshSettingsList
    } = useGetSettingsList();
    const options = [
        ...settingsList.map((set) => { return { name: set.displayName, value: set.name } }),
        createNewOption
    ];

    const {
        getSetting,
        loading: isGetSettingLoading,
        error: getSettingError,
        setting: fetchedSetting
    } = useGetSetting();

    const {
        saveSetting,
        loading: isSaveSettingLoading,
    } = useSaveSetting()

    const {
        deleteSettings,
        loading: isDeleteSettingsLoading,
    } = useDeleteSettings();

    //Component hooks
    const {
        component: selectSettingsComponent,
        selectedOption,
        isOptionSelected,
        setSelection: setSelectedOption
    } = useOptionSelect("Select Endpoint Name", options);

    const {
        component: proxyEditor,
        isValid: isProxyEditorValid,
        proxySettingsData,
        setProxySettings
    } = useProxySettingsEditor(displayErrors);

    const {
        component: promptUser,
        userSelection: userPromptSelection,
        openDialog: openUserPromptDialog,
        isSelected: isUserPromptSelected
    } = useUserPrompt();

    //Effect to fetch setting and update editor
    useEffect(() => {
        if (selectedOption === createNewOption) {
            setDisplayErrors(false);
            setProxySettings(BLANK_PROXY_SETTINGS);
            return;
        }
        if (isOptionSelected) {
            setDisplayErrors(false);
            getSetting(selectedOption);
        }
    }, [selectedOption]);

    useEffect(() => {
        if (selectedOption !== createNewOption
            && isOptionSelected
            && !isGetSettingLoading
            && !getSettingError
        ) {
            setProxySettings(fetchedSetting);
        }
    }, [isGetSettingLoading, fetchedSetting]);

    //Functions for uploading user settings
    const uploadSettings = () => {
        // check if settings are valid
        // if valid, upload settings
        if (!isProxyEditorValid) {
            openUserPromptDialog(
                "Invalid Settings",
                "Please fix the errors in the settings before uploading",
                true
            );
            setDisplayErrors(true);
            return;
        }
        if (settingsList.includes(proxySettingsData.name)) {
            openUserPromptDialog(
                "Overwrite Settings",
                `Are you sure you want to overwrite the settings for ${proxySettingsData.name}?`,
                false
            );
            setUploadFlag(true);
            return;
        }
        saveSetting([proxySettingsData]);
        setOpenUploadDialog(true);
    }

    const deleteSetting = () => {
        if (selectedOption !== createNewOption && isOptionSelected) {
            openUserPromptDialog(
                "Delete Settings",
                `Are you sure you want to delete the settings for ${proxySettingsData.name}?`,
                false
            );
            setDeleteFlag(true);
        }
    }

    useEffect(() => {
        // if user selects yes, delete settings
        if (!isUserPromptSelected || !userPromptSelection) return;
        if (deleteFlag) {
            setDeleteFlag(false);
            console.log("deleting settings");
            deleteSettings([proxySettingsData.name]);
            //set display to blank 
            setProxySettings(BLANK_PROXY_SETTINGS);
            setSelectedOption(createNewOption);
        }
        if (uploadFlag) {
            setUploadFlag(false);
            console.log("uploading settings");
            saveSetting([proxySettingsData]);
            setOpenUploadDialog(true);
        }
    }, [userPromptSelection, isUserPromptSelected]);

    useEffect(() => {
        refreshSettingsList();
    }, [isSaveSettingLoading, isDeleteSettingsLoading]);

    const controlPanel = (<FormWithMargin>
        <Typography>Select Endpoint Configuration</Typography>
        {isSettingsListLoading ? <Loading /> : selectSettingsComponent}
        {isOptionSelected && <Button onClick={uploadSettings}>Save Settings</Button>}
        {selectedOption !== createNewOption && isOptionSelected
            && <Button onClick={deleteSetting}>Delete Settings</Button>}
    </FormWithMargin>)
    return (
        <div>
            {promptUser}
            {controlPanel}
            {!isGetSettingLoading && isOptionSelected && proxyEditor}
            {isGetSettingLoading && <Loading />}
            <UploadDialog
                open={openUploadDialog}
                endpointName={proxySettingsData.name}
                uploading={isSaveSettingLoading}
                closeDialog={closeUploadDialog}
            />
        </div>
    )
}
function App() {
    const [currentPage, setCurrentPage] = useState("");
    const navOptions = [
    ];
    return (
        <div className="App">
            <Header navOptions={navOptions} setNavigationValue={setCurrentPage} />
            {currentPage === "" && <ProxyConfigApp />}
            {currentPage === "logs" && <AuditLogs />}
        </div>
    )
}

export default App;
